import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  MessageCircle,
  X,
  ArrowRight,
  ArrowLeft,
  Compass,
  BookOpen,
  HeartHandshake,
  CalendarCheck,
  PlayCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Care Portal guided chatbox.
 * - Reuses the visual design + interaction pattern from PublicChatbox
 * - Structured navigational options only (no free-form input, no AI, no crisis routing)
 * - Rendered only on non-admin Care Portal pages (gated in Layout.jsx via useLocation)
 * - Does NOT replace Request Support or Schedule a Chaplain — it links to them
 */

const MAIN_OPTIONS = [
  {
    id: "start_here",
    label: "Start Here",
    description: "New to the Care Portal? Read the welcome & orientation.",
    icon: Compass,
    route: "/care-portal/start-here",
  },
  {
    id: "resource_library",
    label: "Resource Library",
    description: "Guides, articles, and materials curated for employees.",
    icon: BookOpen,
    route: "/care-portal/resources",
  },
  {
    id: "request_support",
    label: "Request Support",
    description: "Confidentially request a follow-up from a chaplain.",
    icon: HeartHandshake,
    route: "/care-portal/request-support",
  },
  {
    id: "schedule",
    label: "Schedule a Chaplain",
    description: "Book a video conversation with a CCCHAPS chaplain.",
    icon: CalendarCheck,
    // route resolved dynamically from /api/config; safe fallback below.
    route: null,
    externalKey: "schedule_chaplain_url",
    fallbackRoute: "/care-portal/request-support",
  },
  {
    id: "callachap",
    label: "Talk with a Chaplain — CALLACHAP",
    description: "Call, text, video, or send a confidential message to a CCCHAPS chaplain.",
    icon: HeartHandshake,
    route: "/care-portal/callachap",
  },
  {
    id: "on_demand",
    label: "CCCHAPS On Demand",
    description: "Videos and resources for life, relationships, parenting, finances, well-being, and family.",
    icon: PlayCircle,
    route: "/care-portal/on-demand",
  },
];

export default function CarePortalChatbox() {
  const [open, setOpen] = useState(false);
  const [scheduleUrl, setScheduleUrl] = useState("");
  const launcherRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setScheduleUrl(r.data?.schedule_chaplain_url || "")).catch(() => {});
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setTimeout(() => launcherRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  useEffect(() => {
    if (open) setTimeout(() => firstFocusableRef.current?.focus(), 30);
  }, [open]);

  const handleSelect = (opt) => {
    close();
    // If option has an external URL key, prefer that; otherwise route.
    if (opt.externalKey === "schedule_chaplain_url") {
      if (scheduleUrl) {
        window.open(scheduleUrl, "_blank", "noopener,noreferrer");
      } else {
        navigate(opt.fallbackRoute || "/care-portal/request-support");
      }
      return;
    }
    navigate(opt.route);
  };

  return (
    <>
      {/* Launcher */}
      <button
        ref={launcherRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="care-portal-chatbox-panel"
        data-testid="care-portal-chatbox-launcher"
        className={`fixed z-[60] bottom-5 right-5 md:bottom-6 md:right-6 inline-flex items-center gap-2 rounded-full bg-[#4A6FA5] text-white shadow-lg shadow-slate-900/10 px-5 py-3 text-sm font-medium hover:bg-[#3B597B] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#4A6FA5] transition-transform ${
          open ? "scale-0 opacity-0 pointer-events-none" : "scale-100 opacity-100"
        }`}
      >
        <MessageCircle className="w-4 h-4" strokeWidth={2} />
        How can we help?
      </button>

      {/* Panel */}
      {open && (
        <div
          id="care-portal-chatbox-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby="care-portal-chatbox-title"
          data-testid="care-portal-chatbox-panel"
          className="fixed z-[70] inset-x-3 bottom-3 md:bottom-6 md:right-6 md:inset-x-auto md:w-[380px] max-h-[calc(100vh-2rem)] md:max-h-[640px] rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-slate-900/15 flex flex-col overflow-hidden"
        >
          <header className="flex items-center justify-between gap-2 px-4 py-3 bg-[#4A6FA5] text-white shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="min-w-0">
                <p id="care-portal-chatbox-title" className="font-heading text-sm font-semibold truncate">
                  How can we help?
                </p>
                <p className="text-[11px] text-white/80 truncate">CCCHAPS Care Portal · guided navigation</p>
              </div>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Close chatbox"
              data-testid="care-portal-chatbox-close"
              className="inline-flex items-center justify-center w-8 h-8 rounded hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <X className="w-4 h-4" />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto p-4">
            <div data-testid="care-portal-chatbox-menu">
              <p className="text-xs text-[#3B597B] font-semibold leading-relaxed mb-1" data-testid="care-portal-chatbox-247">
                CCCHAPS chaplain support is 24/7 on-call.
              </p>
              <p className="text-sm text-slate-700 leading-relaxed mb-3">
                Choose where you'd like to go and we'll take you there.
              </p>
              <ul className="space-y-1.5">
                {MAIN_OPTIONS.map((opt, i) => (
                  <li key={opt.id}>
                    <button
                      ref={i === 0 ? firstFocusableRef : null}
                      type="button"
                      onClick={() => handleSelect(opt)}
                      data-testid={`care-portal-chatbox-option-${opt.id}`}
                      className="w-full flex items-start gap-3 text-left rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm hover:border-[#4A6FA5] hover:bg-[#F0F4F8] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4A6FA5]"
                    >
                      <span className="shrink-0 mt-0.5 text-[#4A6FA5]">
                        <opt.icon className="w-4 h-4" strokeWidth={1.75} />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-medium text-slate-900">{opt.label}</span>
                        <span className="block mt-0.5 text-[12px] text-slate-500 leading-snug">{opt.description}</span>
                      </span>
                      <ArrowRight className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <footer className="px-4 py-2.5 border-t border-slate-100 bg-slate-50 text-[10.5px] leading-snug text-slate-500 shrink-0">
            This chatbox is a navigation aid — not an emergency service or a mental-health crisis line. In an emergency, call 911.
          </footer>
        </div>
      )}
    </>
  );
}
